import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { sendChatMessage, getChatSuggestions, getDailySummary, getWeeklySummary, getChildSummary, fetchFamilyMembers } from '../lib/api';
import api from '../lib/api';
import { format } from 'date-fns';
import { 
  Send, Sparkles, Loader2, MessageCircle, Calendar as CalendarIcon,
  RefreshCw, Copy, Share2, Users, Clock, ChevronDown, Zap, Lock, Crown, Brain
} from 'lucide-react';
import { toast } from 'sonner';

// Floating Widget
const FloatingWidget = ({ children, className = '' }) => (
  <div
    className={`relative ${className}`}
    style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(40px)',
      WebkitBackdropFilter: 'blur(40px)',
      borderRadius: '16px',
      boxShadow: '0 2px 16px -4px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03)',
    }}
  >
    {children}
  </div>
);

// Chat Bubble
const ChatBubble = ({ message, isUser, timestamp }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`max-w-[85%]`}>
      <div
        className={`px-4 py-3 ${
          isUser
            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl rounded-br-md'
            : 'bg-gray-100 text-gray-900 rounded-2xl rounded-bl-md'
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message}</p>
      </div>
      {timestamp && (
        <p className={`text-[11px] text-gray-400 mt-1 ${isUser ? 'text-right mr-2' : 'ml-2'}`}>
          {format(new Date(timestamp), 'h:mm a')}
        </p>
      )}
    </div>
  </div>
);

// Quick Action Chip
const QuickChip = ({ icon: Icon, label, onClick, active, gradient }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
      active 
        ? 'text-white shadow-lg' 
        : 'bg-white text-gray-700 shadow-sm hover:shadow-md border border-gray-100'
    }`}
    style={active ? { background: gradient || 'linear-gradient(135deg, #8B5CF6, #EC4899)' } : {}}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

const SmartDaddyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [activeMode, setActiveMode] = useState('chat'); // 'chat', 'daily', 'weekly', 'child'
  
  // Account status
  const [accountStatus, setAccountStatus] = useState(null);
  const [checkingAccount, setCheckingAccount] = useState(true);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Summary state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedChild, setSelectedChild] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    checkAccountAndLoadData();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const checkAccountAndLoadData = async () => {
    try {
      // Check account status first
      const accountRes = await api.get('/account/status');
      setAccountStatus(accountRes.data);
      
      // Only load AI data if user is Pro
      if (accountRes.data.account_type === 'pro') {
        const [members, sug] = await Promise.all([
          fetchFamilyMembers(),
          getChatSuggestions()
        ]);
        setFamilyMembers(members);
        setSuggestions(sug);
        if (members.length > 0) {
          setSelectedChild(members[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setCheckingAccount(false);
      setLoading(false);
    }
  };

  // Show upgrade prompt for free users
  if (!checkingAccount && accountStatus?.account_type !== 'pro') {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center mb-6">
            <Lock className="h-10 w-10 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Unlock SmartDaddy AI
          </h1>
          <p className="text-gray-600 max-w-md mx-auto mb-8">
            Get your personal AI-powered family assistant. Ask questions about schedules, 
            get daily and weekly summaries, and let AI help you stay organized.
          </p>
          
          {/* Feature Preview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
              <MessageCircle className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <p className="text-sm font-medium">Ask Anything</p>
              <p className="text-xs text-gray-500 mt-1">Chat about your family's schedule</p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
              <CalendarIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-sm font-medium">Daily Summaries</p>
              <p className="text-xs text-gray-500 mt-1">AI-generated daily overviews</p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
              <Users className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-medium">Per-Child Insights</p>
              <p className="text-xs text-gray-500 mt-1">Summaries for each kid</p>
            </div>
          </div>

          {/* Example conversation preview */}
          <div className="max-w-md mx-auto mb-8 p-4 bg-gray-50 rounded-2xl">
            <div className="flex justify-end mb-2">
              <div className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl rounded-br-md text-sm">
                What's on the schedule for tomorrow?
              </div>
            </div>
            <div className="flex justify-start">
              <div className="px-4 py-2 bg-white text-gray-700 rounded-2xl rounded-bl-md text-sm shadow-sm">
                Tomorrow is busy! Emma has soccer practice at 4pm and Jake has swim lessons at 5:30pm...
              </div>
            </div>
          </div>
          
          <Button 
            disabled
            className="rounded-full h-12 px-8 bg-gray-300 cursor-not-allowed opacity-60"
          >
            <Crown className="h-5 w-5 mr-2" />
            Pro — Coming Soon
          </Button>
          
          <p className="text-sm text-gray-500 mt-4">
            We're working on bringing you amazing AI features. Stay tuned!
          </p>
        </div>
      </div>
    );
  }

  const loadInitialData = async () => {
    try {
      const [members, sug] = await Promise.all([
        fetchFamilyMembers(),
        getChatSuggestions()
      ]);
      setFamilyMembers(members);
      setSuggestions(sug);
      if (members.length > 0) {
        setSelectedChild(members[0].id);
      }
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Chat functions
  const sendMessage = async (text) => {
    const messageText = text || input.trim();
    if (!messageText || chatLoading) return;

    const userMessage = {
      id: Date.now(),
      text: messageText,
      isUser: true,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setChatLoading(true);
    setActiveMode('chat');

    try {
      const response = await sendChatMessage(messageText);
      const assistantMessage = {
        id: Date.now() + 1,
        text: response.response,
        isUser: false,
        timestamp: response.timestamp
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error('Failed to get response');
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setChatLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Summary functions
  const generateSummary = async (type) => {
    setActiveMode(type);
    setSummaryLoading(true);
    setSummary(null);
    
    try {
      let result;
      if (type === 'daily') {
        result = await getDailySummary(format(selectedDate, 'yyyy-MM-dd'));
      } else if (type === 'weekly') {
        result = await getWeeklySummary(format(selectedDate, 'yyyy-MM-dd'));
      } else if (type === 'child' && selectedChild) {
        result = await getChildSummary(selectedChild);
      }
      setSummary(result);
      toast.success('Summary generated!');
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const copySummary = () => {
    if (summary?.summary) {
      navigator.clipboard.writeText(summary.summary);
      toast.success('Copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-[500px] rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-8">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
            <Zap className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SmartDaddy</h1>
            <p className="text-sm text-gray-500">AI-powered family assistant</p>
          </div>
        </div>
      </div>

      {/* Quick Actions - Horizontal Scroll */}
      <div className="flex gap-2 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0">
        <QuickChip
          icon={MessageCircle}
          label="Ask AI"
          onClick={() => { setActiveMode('chat'); setSummary(null); }}
          active={activeMode === 'chat'}
        />
        <QuickChip
          icon={CalendarIcon}
          label="Daily Summary"
          onClick={() => { setActiveMode('daily'); setSummary(null); }}
          active={activeMode === 'daily'}
          gradient="linear-gradient(135deg, #3B82F6, #8B5CF6)"
        />
        <QuickChip
          icon={CalendarIcon}
          label="Weekly Summary"
          onClick={() => { setActiveMode('weekly'); setSummary(null); }}
          active={activeMode === 'weekly'}
          gradient="linear-gradient(135deg, #10B981, #3B82F6)"
        />
        {familyMembers.length > 0 && (
          <QuickChip
            icon={Users}
            label="Child Summary"
            onClick={() => { setActiveMode('child'); setSummary(null); }}
            active={activeMode === 'child'}
            gradient="linear-gradient(135deg, #F59E0B, #EF4444)"
          />
        )}
      </div>

      {/* Main Content Area */}
      <FloatingWidget className="overflow-hidden">
        {/* Summary Results */}
        {(activeMode === 'daily' || activeMode === 'weekly' || activeMode === 'child') && (
          <div className="p-4">
            {/* Summary Header */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${
                  activeMode === 'daily' ? 'bg-gradient-to-br from-blue-500 to-purple-600' :
                  activeMode === 'weekly' ? 'bg-gradient-to-br from-green-500 to-blue-600' :
                  'bg-gradient-to-br from-orange-500 to-red-600'
                }`}>
                  {activeMode === 'child' ? <Users className="h-5 w-5" /> : <CalendarIcon className="h-5 w-5" />}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">
                    {activeMode === 'daily' ? 'Daily Summary' : 
                     activeMode === 'weekly' ? 'Weekly Overview' : 
                     'Child Summary'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {activeMode === 'child' && selectedChild
                      ? familyMembers.find(m => m.id === selectedChild)?.name
                      : format(selectedDate, activeMode === 'daily' ? 'EEEE, MMMM d' : "'Week of' MMM d, yyyy")}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {activeMode !== 'child' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-xl h-9">
                        <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                        Change
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} />
                    </PopoverContent>
                  </Popover>
                )}
                
                {activeMode === 'child' && familyMembers.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-xl h-9">
                        <Users className="mr-1.5 h-3.5 w-3.5" />
                        {familyMembers.find(m => m.id === selectedChild)?.name || 'Select'}
                        <ChevronDown className="ml-1 h-3 w-3" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="end">
                      {familyMembers.map(member => (
                        <button
                          key={member.id}
                          onClick={() => setSelectedChild(member.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedChild === member.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: member.color }}>
                            {member.name?.charAt(0)}
                          </div>
                          {member.name}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                )}
                
                <Button
                  size="sm"
                  className="rounded-xl h-9 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  onClick={() => generateSummary(activeMode)}
                  disabled={summaryLoading}
                >
                  {summaryLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : summary ? (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  {summary ? 'Refresh' : 'Generate'}
                </Button>
              </div>
            </div>

            {/* Summary Content */}
            {summaryLoading ? (
              <div className="space-y-3 py-8">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : summary ? (
              <div className="space-y-4">
                  <div className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-xl">
                  {summary.summary}
                </div>
                
                {summary.events?.length > 0 && (
                  <div className="flex gap-3 flex-wrap">
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-xl">
                      <CalendarIcon className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">{summary.events.length} Events</span>
                    </div>
                    {summary.events.length > 3 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-xl">
                        <Clock className="h-4 w-4 text-amber-500" />
                        <span className="text-sm font-medium text-amber-700">Busy Schedule</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <Button variant="outline" size="sm" className="rounded-xl flex-1 h-10" onClick={copySummary}>
                    <Copy className="mr-2 h-4 w-4" /> Copy
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-xl flex-1 h-10" onClick={() => toast.info('Share feature coming soon!')}>
                    <Share2 className="mr-2 h-4 w-4" /> Share
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Sparkles className="h-12 w-12 mx-auto text-gray-200 mb-3" />
                <p className="text-gray-500 text-sm mb-1">Ready to generate your summary</p>
                <p className="text-gray-400 text-xs">Click the "Generate" button above to get started</p>
              </div>
            )}
          </div>
        )}

        {/* Chat Interface */}
        {activeMode === 'chat' && (
          <div className="flex flex-col" style={{ height: '500px' }}>
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-4">
                    <MessageCircle className="h-8 w-8 text-purple-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Hi, I'm SmartDaddy</h2>
                  <p className="text-gray-500 text-sm mb-6 max-w-sm">
                    Ask me anything about your family's schedule, conflicts, or upcoming events.
                  </p>
                  {suggestions.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-400">Try asking:</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {suggestions.slice(0, 4).map((sug, i) => (
                          <button
                            key={i}
                            onClick={() => sendMessage(sug)}
                            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
                          >
                            {sug}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {messages.map((msg) => (
                    <ChatBubble
                      key={msg.id}
                      message={msg.text}
                      isUser={msg.isUser}
                      timestamp={msg.timestamp}
                    />
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start mb-3">
                      <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-3 border-t border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask SmartDaddy anything..."
                  className="flex-1 h-11 rounded-xl border-gray-200 bg-white focus:ring-purple-500/20 focus:border-purple-500"
                  disabled={chatLoading}
                />
                <Button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || chatLoading}
                  size="icon"
                  className="rounded-xl h-11 w-11 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
                >
                  {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}
      </FloatingWidget>

      {/* Tips */}
      <FloatingWidget className="mt-4 p-4 border border-dashed border-gray-200">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-purple-500" />
          </div>
          <div>
            <h4 className="font-medium text-sm text-gray-900 mb-1">SmartDaddy Tips</h4>
            <ul className="text-xs text-gray-500 space-y-0.5">
              <li>Ask "What's on today?" for quick schedule overview</li>
              <li>Generate daily/weekly summaries with one tap</li>
              <li>Ask about conflicts or busy periods</li>
            </ul>
          </div>
        </div>
      </FloatingWidget>
    </div>
  );
};

export default SmartDaddyPage;
